import React from 'react';

import { KioskType } from 'types/KioskTypes';

import { useDispatch, useSelector } from 'hooks';
import { useCheckQRCodeTokenMutation, useSendQrCodeMutation } from 'modules/auth/service';
import { QR_CODE_ACTION_MAP, isQrCodeActionType } from 'modules/sockets/actions';

import { getKioskId, getScannerKioskContext } from 'modules/kiosk/selectors';
import { getKioskType } from 'modules/dealers/selectors';

import QRCodeScannerDevice from 'models/QRCodeScannerDevice';
import { QrCodeLoginMethod } from 'modules/auth/types/LoginRequest';

/**
 * This custom hook works only on Kiosk with Edge browser, Edge policy "WebHidAllowDevicesForUrls" enabled and in HTTPS.
 * @see https://fleetback.atlassian.net/wiki/x/WhcdAQ
 */
const useQrCodeScanner = (scannerIsDisabled: boolean = false, onQrCodeSuccess?: () => void) => {
  const dispatch = useDispatch();
  const kioskId = useSelector(getKioskId);
  const kioskType = useSelector(getKioskType);
  // Attached to the Sentry report sent when the scanner cannot be connected
  const scannerContext = useSelector(getScannerKioskContext);

  const isScannerEnabled = Boolean(kioskId) && kioskType === KioskType.PARTTEAM && !scannerIsDisabled;

  const [login] = useCheckQRCodeTokenMutation();
  const [qrCodeLogin] = useSendQrCodeMutation();

  const handleLogin = React.useCallback(async (qrScannerValue: string) => {
    console.log('[QR Code HID] - Handle Login is called');
    try {
      const qrObject = JSON.parse(qrScannerValue);
      const { type, payload, code } = qrObject;
      console.log('[QR Code HID] - code was properly parsed');
      if (isQrCodeActionType(type) && payload) {
        const hasData = Object.keys(payload).length;
        if (hasData) {
          console.log('[QR Code HID] - Migration code - new shape validated');
          dispatch(QR_CODE_ACTION_MAP[type](payload));
        }
      } else if (code && type) {
        // Legacy: { code, type: SelfServiceType }
        console.log('[QR Code HID] - Migration code - legacy shape validated');
        await qrCodeLogin({ qrCode: code, type, loginMethod: QrCodeLoginMethod.QR_CODE }).unwrap();
        console.log('[QR Code HID] - Migration code - qrCode login has been called');
        onQrCodeSuccess?.();
      }
    } catch {
      // Legacy: raw bearer token
      const isBearerToken = qrScannerValue.length > 50;
      console.log('[QR Code HID] - Not an object - String only');
      if (isBearerToken && kioskId) {
        login({ qrCodeToken: qrScannerValue, kioskId });
      }
    }
  }, [dispatch, login, kioskId, qrCodeLogin, onQrCodeSuccess]);

  // Declared first: the scanner must be created with the kiosk context, before its first connection attempt.
  // Kept apart from the listener effect: a kiosk context update must not re-subscribe the listener.
  React.useEffect(() => {
    if (isScannerEnabled) {
      QRCodeScannerDevice.getInstance(scannerContext).setKioskContext(scannerContext);
    }
  }, [isScannerEnabled, scannerContext]);

  React.useEffect(() => {
    if (isScannerEnabled) {
      console.log('[useQrCodeScanner] scanner listener ADDED');
      const qrCodeScanner = QRCodeScannerDevice.getInstance();

      qrCodeScanner.addListener(handleLogin);
      return () => {
        console.log('[useQrCodeScanner] scanner listener REMOVED');
        qrCodeScanner.removeListener(handleLogin);
      };
    }

    return undefined;
  }, [isScannerEnabled, handleLogin]);
};

export default useQrCodeScanner;
